/* eslint-disable camelcase */



/** ************************************
 * HELPERS - ITEM
 */

const createBaseItem = ({ customer, jobNumber, lineName, surveys }) => {
	const _item = {
		customer,
		jobNumber,
		lineName,
		surveys,
	};

	return _item;
}

const _createKey = ({ customer, jobNumber, lineName }) => {
	return `${customer}~${jobNumber}~${lineName}`;
}

const _getCurrentItem = ({ autoFixStore, customer, jobNumber, lineName }) => {
	const key = _createKey({customer, jobNumber, lineName});
	return autoFixStore._map[key];
}

const _didUserChange = (item, newItem, key) => {
	if ((!item?.[key] && newItem?.[key]) || (item?.[key] && !newItem?.[key])) {
		return true;
	}
	if (!item?.[key] && !newItem?.[key]) {
		return false;
	}
	return item?.[key]?.from !== newItem[key].from || item?.[key]?.to !== newItem[key].to;
}

const didChange = (item, newItem) => {
	const keysToCheck = [
		newItem.ct ? 'ct' : undefined,
		newItem.pm ? 'pm' : undefined,		
	].filter(v => v);

	return keysToCheck.some(key => _didUserChange(item, newItem, key));
}



/** ************************************
 * HELPERS - MAP & LIST
 */

const _updateItem = ({ autoFixStore, key, newItem }) => {
	const { _map, _list } = autoFixStore;
	const cloneMap = { ..._map };
	const cloneList = [..._list];
	const item = _map[key];
	const exists = !!item;

	if (!didChange(item, newItem)) {
		return autoFixStore;
	}

	if (exists) {
		const idx = cloneList.indexOf(item);
		if (idx > -1) {
			cloneMap[key] = newItem;
			cloneList[idx] = newItem;
		} else {
			cloneMap[key] = newItem;
			cloneList.push(newItem);
		}
	} else {
		cloneMap[key] = newItem;
		cloneList.push(newItem);
	}

	return {
		...autoFixStore,
		_map: cloneMap,
		_list: cloneList,
	}
}

const _removeItem = ({ autoFixStore, key }) => {
	const { _map, _list } = autoFixStore;
	const cloneMap = { ..._map };
	const cloneList = [..._list];
	const item = autoFixStore._map[key];
	const exists = !!item;

	if (exists) {
		delete cloneMap[key];
		const idx = cloneList.indexOf(item);
		if (idx > -1) {
			cloneList.splice(idx, 1);
		}
	}

	return !exists ? autoFixStore : {
		...autoFixStore,
		_map: cloneMap,
		_list: cloneList,
	}
}



/** ************************************
 * ASSERTS
 */

const assertAutoFixStore = (autoFixStore) => {
	if (!autoFixStore) {
		throw new Error('autoFixStore is required');
	}
	if (!autoFixStore?._map) {
		throw new Error('autoFixStore prop _map is not defined');
	}
	if (!autoFixStore?._list) {
		throw new Error('autoFixStore prop _map is not defined');
	}
}

const assertCustomer = (customer) => {
	if (!customer) {
		throw new Error('customer is required');
	}
}

const assertJobNumber = (jobNumber) => {
	if (!jobNumber) {
		throw new Error('jobNumber is required');
	}
}

const assertLineName = (lineName) => {
	if (!lineName) {
		throw new Error('lineName is required');
	}
}

const assertSurveys = (surveys) => {
	if (!surveys) {
		throw new Error('surveys is required');
	}
	if (!(surveys instanceof Array)) {
		throw new Error('surveys must be an array');
	}
}

const assertCtPmObjs = (ct, pm) => {
	if (!ct && !pm) {
		throw new Error('one of the following is required: ct or pm');
	}
	if (ct && (!ct?.from || !ct?.to)) {
		throw new Error('ct object is not formatted correctly')
	}
	if (pm && (!pm?.from || !pm?.to)) {
		throw new Error('pm object is not formatted correctly')
	}
}



/** ************************************
 * MAIN
 */

const clearAutoFixPmCt = () => ({
	_map: {},
	_list: [],
	isFixing: false,
	isStopping: false,
});

const addAutoFixPmCt = ({ autoFixStore, customer, jobNumber, lineName, surveys, ct, pm } = {}) => {
	try {
		assertAutoFixStore(autoFixStore);
		assertCustomer(customer);
		assertJobNumber(jobNumber);
		assertLineName(lineName);
		assertSurveys(surveys);
		assertCtPmObjs(ct, pm);

		const key = _createKey({ customer, jobNumber, lineName });
		const item = _getCurrentItem({ autoFixStore, customer, jobNumber, lineName });
		const exists = !!item;
		const newItem = exists ? {...item} : createBaseItem({ customer, jobNumber, lineName, surveys });

		if (ct) {
			newItem.ct = ct;
		}

		if (pm) {
			newItem.pm = pm;
		}

		return _updateItem({ autoFixStore, key, newItem });
	} catch (e) {
		console.warn(e);
		return autoFixStore;
	}
}

const removeAutoFixPmCt = ({ autoFixStore, customer, jobNumber, lineName, surveys } = {}) => {
	try {
		assertAutoFixStore(autoFixStore);
		assertCustomer(customer);
		assertJobNumber(jobNumber);
		assertLineName(lineName);
		assertSurveys(surveys);

		const key = _createKey({ customer, jobNumber, lineName });

		return _removeItem({ autoFixStore, key });		
	} catch (e) {
		console.warn(e);
		return autoFixStore;
	}
}

const UTIL = {
	_createKey,
	clearAutoFixPmCt,
	addAutoFixPmCt,
	removeAutoFixPmCt,
}

export default UTIL;